import { AudioProcessOutput, UserSettings, AudioCommand, AudioProcessError } from "../../wasm/pkg/wasm";
import {
  AudioWebWorkerInitSettings,
  AudioWebWorkerInputControls,
  CompleteAudioBlobToWasm,
  MSG_FROM_WASM,
  MSG_TO_WASM,
  PartialAudioBlobToWasm,
} from "../models/PostMessages.interface";

let worker: Worker | undefined = undefined;
let controls: AudioWebWorkerInputControls | undefined = undefined;

/**
 * Allow the UI and audio inputs to communicate with the loaded web worker,
 * via a set of callbacks corresponding to postMessages to/from the worker.
 */
export function connectToAudioWebWorker({ onInit, onError, onLatestMusicalData }: AudioWebWorkerInitSettings): AudioWebWorkerInputControls {
  if (!window.Worker) {
    throw new Error("This browser does not support webworkers.  Please use a modern web browser.");
  }

  if (!worker) {
    worker = new Worker(new URL("../worker/initAudioWebWorker.ts", import.meta.url), { name: "audio-worker" });
  }
  /*
   * This wouldn't need a re-do during normal operation, but because componentDidMount can run again
   * during react dev it's important that the latest callbacks be connected to the worker.
   */
  worker.onmessage = async ({ data }) => {
    // console.log("onmessage from audio-worker", data);
    if (data?.message === MSG_FROM_WASM.worker_ready) {
      onInit();
    } else if (data?.message === MSG_FROM_WASM.worker_error) {
      console.error("worker_error", data);
      onError(data?.payload as AudioProcessError);
    } else if (data?.message === MSG_FROM_WASM.worker_music_data) {
      // console.log("data from wasm", data?.payload);
      onLatestMusicalData(data?.payload as AudioProcessOutput);
    }
  };
  if (controls) {
    return controls;
  }

  const sendNextPartialAudioBlob = (payload: PartialAudioBlobToWasm) => {
    worker!.postMessage({
      message: MSG_TO_WASM.partial_audio,
      payload,
    });
  };

  const sendCompleteAudioBlob = (payload: CompleteAudioBlobToWasm) => {
    worker!.postMessage({
      message: MSG_TO_WASM.complete_audio,
      payload,
    });
  };

  const resetAudioState = () => {
    worker!.postMessage({ message: MSG_TO_WASM.cmd, payload: "Exit" as AudioCommand });
  };

  const updateSettings = (UpdateSettings: UserSettings) => {
    worker!.postMessage({ message: MSG_TO_WASM.cmd, payload: { UpdateSettings } as AudioCommand });
  };

  const audioTimeSeekJump = (integerSeekTime: number) => {
    worker!.postMessage({ message: MSG_TO_WASM.cmd, payload: { TimeSeek: integerSeekTime } as AudioCommand });
  };

  // if necessary: worker.terminate();

  controls = {
    resetAudioState,
    updateSettings,
    sendNextPartialAudioBlob,
    sendCompleteAudioBlob,
    audioTimeSeekJump,
  };
  return controls;
}
